import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

/**
 * Surfaces offline-sync losses that used to be silent.
 *
 * `lib/offlineSync.ts` queues workouts finished offline and replays them on
 * reconnect. If the server rejects one (a 4xx, or a 5xx past the retry cap) the
 * queue drops it — previously with NO signal, so a user who was told "saved
 * offline, will sync later" could lose the workout and never know (audit
 * 2026-08-08). The queue now fires a `trybeup:offline-sync-dropped` window
 * event; this listener turns it into a visible, honest toast so the person can
 * re-log it. Mounted once at the app root (App.tsx).
 */
export default function OfflineSyncDropListener() {
  const { toast } = useToast();

  useEffect(() => {
    const onDropped = (e: Event) => {
      const count = (e as CustomEvent<{ count?: number }>).detail?.count ?? 1;
      // Copy is generic: this queue carries workouts AND other offline writes
      // (offlineApiRequest enqueues arbitrary non-GET requests), so it must not
      // claim "a workout" for what might be something else.
      toast({
        title: count > 1 ? "Some changes couldn't be saved" : "A change couldn't be saved",
        description:
          "We couldn't sync " +
          (count > 1 ? `${count} changes` : "a change") +
          " to the server. Please check that everything saved, and re-enter anything missing.",
        variant: "destructive",
      });
    };
    window.addEventListener("trybeup:offline-sync-dropped", onDropped);
    return () => window.removeEventListener("trybeup:offline-sync-dropped", onDropped);
  }, [toast]);

  return null;
}
